import { US_REGIONS } from "@ferrivia/api-contract";
import { Field, Select, SelectContent, SelectItem, SelectTrigger, Text } from "@ferrivia/ui";
import { useMarket, useTranslation } from "@ferrivia/i18n";
import { StepLayout } from "../components/StepLayout";
import { focusWithoutPan } from "../focusWithoutPan";
import s from "./LocationStep.module.css";

// The prototype's two, carried over as-is. Widening the list is a content
// decision rather than a code one — the column is a plain ISO 3166-1 alpha-2
// code, so adding entries here is the whole change.
const COUNTRY_CODES = ["FI", "US"] as const;

export interface LocationStepProps {
  country: string | null;
  city: string;
  region?: string | null;
  onRegionChange?: (region: string) => void;
  onCountryChange: (country: string) => void;
  onCityChange: (city: string) => void;
  onSubmit: () => void;
}

export function LocationStep({
  country,
  city,
  region,
  onRegionChange,
  onCountryChange,
  onCityChange,
  onSubmit,
}: LocationStepProps) {
  const { t } = useTranslation("client");
  const us = useMarket() === "us";

  // i18next-parser only extracts literal-key t() calls, so this is a static
  // lookup rather than a template key — same pattern as TabBar's nav labels.
  const countryLabels: Record<(typeof COUNTRY_CODES)[number], string> = {
    FI: t("onboarding.location.countries.FI", "Suomi"),
    US: t("onboarding.location.countries.US", "United States"),
  };

  return (
    <StepLayout title={t("onboarding.location.title", "Missä asut?")}>
      {us ? (
        <div className={s.field}>
          <Text as="label" size="sm" weight="medium" id="region-label" className={s.label}>
            {t("onboarding.location.stateLabel", "Osavaltio")}
          </Text>
          <Select value={region ?? undefined} onValueChange={(value) => onRegionChange?.(value)}>
            <SelectTrigger
              aria-labelledby="region-label"
              placeholder={t("onboarding.location.statePlaceholder", "Valitse osavaltio")}
            />
            <SelectContent>
              {US_REGIONS.map(({ code, name }) => (
                <SelectItem key={code} value={code}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div className={s.field}>
          <Text as="label" size="sm" weight="medium" id="country-label" className={s.label}>
            {t("onboarding.location.countryLabel", "Maa")}
          </Text>
          <Select value={country ?? undefined} onValueChange={onCountryChange}>
            <SelectTrigger
              aria-labelledby="country-label"
              placeholder={t("onboarding.location.countryPlaceholder", "Valitse maa")}
            />
            <SelectContent>
              {COUNTRY_CODES.map((code) => (
                <SelectItem key={code} value={code}>
                  {countryLabels[code]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {us ? null : (
        <Field
          label={t("onboarding.location.cityLabel", "Kaupunki")}
          placeholder={t("onboarding.location.cityPlaceholder", "esim. Helsinki")}
          autoComplete="address-level2"
          value={city}
          onChange={(e) => onCityChange(e.target.value)}
          onMouseDown={focusWithoutPan}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSubmit();
          }}
        />
      )}
    </StepLayout>
  );
}
